import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { formatarDataBR } from '../common/data';

/**
 * O que exige ação na infraestrutura hoje.
 *
 * O número grande da esquerda é `riscoSegurancaAbertos`, e ele existe por um
 * motivo específico: numa creche, "algo quebrado" vai de lâmpada queimada a
 * portão da rua que não tranca. Uma lista ordenada por data trataria os dois
 * igual. Enquanto esse número for maior que zero, há alguma coisa na creche
 * capaz de machucar uma criança hoje — e é a primeira coisa que a tela diz.
 *
 * A segunda seção fecha o buraco entre "o fiscal apontou" e "alguém consertou":
 * não conformidade de vistoria que ainda não virou chamado aparece aqui com um
 * botão que a transforma em trabalho, sem redigitar o texto do fiscal.
 *
 * Situação e texto de prazo vêm CALCULADOS do backend
 * (`infraestrutura-calculos.ts`). A tela não recalcula data — repetir a conta
 * aqui abriria a segunda porta pela qual o erro de fuso do PR #82 entrou.
 */

export interface ChamadoResumido {
  id: number;
  titulo: string;
  categoria: string;
  local: string | null;
  prioridade: 'risco_seguranca' | 'alta' | 'media' | 'baixa';
  status: string;
  prazo: string | null;
  situacao: 'atrasado' | 'vence_hoje' | 'no_prazo' | 'sem_prazo' | 'encerrado';
  diasRestantes: number | null;
  prazoTexto: string;
  responsavel: string | null;
}

interface PendenciaVistoria {
  id: number;
  id_vistoria: number;
  categoria: string;
  descricao: string;
  prazo_correcao: string | null;
}

export interface ResumoInfra {
  hoje: string;
  total: number;
  abertos: number;
  atrasados: number;
  venceHoje: number;
  semPrazo: number;
  riscoSegurancaAbertos: number;
  resolvidos: number;
  tempoMedioResolucaoDias: number | null;
  porPrioridade: Record<string, number>;
  porCategoria: Record<string, number>;
  urgentes: ChamadoResumido[];
  patrimonio: { total: number; porEstado: Record<string, number> };
  pendentesDeVistoria: PendenciaVistoria[];
}

const card: React.CSSProperties = {
  background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16, marginBottom: 20,
};

export const COR_PRIORIDADE: Record<string, string> = {
  risco_seguranca: '#b71c1c', alta: '#e65100', media: '#f9a825', baixa: '#616161',
};

export const LABEL_PRIORIDADE: Record<string, string> = {
  risco_seguranca: 'Risco à segurança', alta: 'Alta', media: 'Média', baixa: 'Baixa',
};

export const LABEL_CATEGORIA: Record<string, string> = {
  eletrica: 'Elétrica', hidraulica: 'Hidráulica', estrutural: 'Estrutural',
  mobiliario: 'Mobiliário', equipamento: 'Equipamento', climatizacao: 'Climatização',
  area_externa: 'Área externa', outro: 'Outro',
};

export const LABEL_STATUS: Record<string, string> = {
  aberto: 'Aberto', em_andamento: 'Em andamento', aguardando_terceiro: 'Aguardando terceiro',
  resolvido: 'Resolvido', cancelado: 'Cancelado',
};

export const LABEL_ESTADO_PATRIMONIO: Record<string, string> = {
  em_uso: 'Em uso', em_manutencao: 'Em manutenção', inutilizado: 'Inutilizado', descartado: 'Descartado',
};

const COR_SITUACAO: Record<string, string> = {
  atrasado: '#c62828', vence_hoje: '#e65100', no_prazo: '#2e7d32',
  sem_prazo: '#616161', encerrado: '#9e9e9e',
};

function Numero({ valor, label, cor }: { valor: React.ReactNode; label: string; cor: string }) {
  return (
    <div style={{ minWidth: 120 }}>
      <div style={{ fontSize: 28, fontWeight: 700, color: cor, lineHeight: 1.1 }}>{valor}</div>
      <div style={{ fontSize: 12, color: '#666' }}>{label}</div>
    </div>
  );
}

export default function PainelInfraestrutura({
  recarregarEm, aoAbrirChamado, aoMudar,
}: {
  recarregarEm?: number;
  aoAbrirChamado?: (id: number) => void;
  aoMudar?: () => void;
}) {
  const [resumo, setResumo] = useState<ResumoInfra | null>(null);
  const [erro, setErro] = useState('');
  const [gerando, setGerando] = useState<number | null>(null);

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get<ResumoInfra>('/infraestrutura/resumo');
      setResumo(data);
      setErro('');
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível carregar o resumo da infraestrutura.');
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar, recarregarEm]);

  async function gerarChamado(item: PendenciaVistoria) {
    setGerando(item.id);
    try {
      const { data } = await api.post(`/infraestrutura/vistoria-itens/${item.id}/chamado`, {});
      await carregar();
      aoMudar?.();
      if (data?.id) aoAbrirChamado?.(data.id);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível abrir o chamado a partir da vistoria.');
    } finally {
      setGerando(null);
    }
  }

  if (erro && !resumo) return <div style={{ ...card, color: '#c62828' }}>{erro}</div>;
  if (!resumo) return <div style={{ ...card, color: '#666' }}>Carregando o resumo…</div>;

  const temRisco = resumo.riscoSegurancaAbertos > 0;

  return (
    <>
      {erro && <div style={{ ...card, color: '#c62828' }}>{erro}</div>}

      <div style={{ ...card, borderLeft: temRisco ? '5px solid #b71c1c' : '5px solid #2e7d32' }}>
        <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Numero
            valor={resumo.riscoSegurancaAbertos}
            label="risco à segurança em aberto"
            cor={temRisco ? '#b71c1c' : '#2e7d32'}
          />
          <Numero valor={resumo.atrasados} label="fora do prazo" cor={resumo.atrasados ? '#c62828' : '#616161'} />
          <Numero valor={resumo.venceHoje} label="vencem hoje" cor={resumo.venceHoje ? '#e65100' : '#616161'} />
          <Numero valor={resumo.abertos} label="chamados abertos" cor="#1565c0" />
          <Numero valor={resumo.semPrazo} label="sem prazo definido" cor={resumo.semPrazo ? '#e65100' : '#616161'} />
          <Numero
            valor={resumo.tempoMedioResolucaoDias === null ? '—' : `${resumo.tempoMedioResolucaoDias}d`}
            label="tempo médio de resolução"
            cor="#616161"
          />
        </div>

        {temRisco && (
          <p style={{ margin: '14px 0 0', color: '#b71c1c', fontWeight: 600 }}>
            Há {resumo.riscoSegurancaAbertos === 1 ? 'um chamado' : `${resumo.riscoSegurancaAbertos} chamados`} classificado
            {resumo.riscoSegurancaAbertos === 1 ? '' : 's'} como risco à segurança sem solução registrada.
          </p>
        )}

        {resumo.urgentes.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <strong style={{ fontSize: 13, color: '#444' }}>Primeiro da fila</strong>
            <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }}>
              {resumo.urgentes.map((c) => (
                <li
                  key={c.id}
                  onClick={() => aoAbrirChamado?.(c.id)}
                  style={{
                    padding: '8px 0', borderTop: '1px solid #f0f0f0',
                    cursor: aoAbrirChamado ? 'pointer' : 'default',
                    display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap',
                  }}
                >
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: '#fff', background: COR_PRIORIDADE[c.prioridade],
                    borderRadius: 4, padding: '2px 6px', whiteSpace: 'nowrap',
                  }}>
                    {LABEL_PRIORIDADE[c.prioridade]}
                  </span>
                  <span style={{ fontWeight: 600 }}>{c.titulo}</span>
                  {c.local && <span style={{ color: '#777', fontSize: 13 }}>· {c.local}</span>}
                  <span style={{ marginLeft: 'auto', color: COR_SITUACAO[c.situacao], fontSize: 13, fontWeight: 600 }}>
                    {c.prazoTexto}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {resumo.pendentesDeVistoria.length > 0 && (
        <div style={{ ...card, borderLeft: '5px solid #e65100' }}>
          <strong>Apontamentos de vistoria sem chamado aberto</strong>
          <p style={{ margin: '4px 0 12px', color: '#666', fontSize: 13 }}>
            O fiscal registrou a não conformidade e ninguém abriu o conserto. Gerar o chamado aproveita o
            texto e o prazo do apontamento — a data foi dada pelo órgão, não é a creche que escolhe.
          </p>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {resumo.pendentesDeVistoria.map((item) => (
              <li key={item.id} style={{
                padding: '10px 0', borderTop: '1px solid #f0f0f0',
                display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
              }}>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div style={{ fontWeight: 600 }}>{item.descricao}</div>
                  <div style={{ color: '#777', fontSize: 12 }}>
                    {item.categoria}
                    {item.prazo_correcao && ` · corrigir até ${formatarDataBR(item.prazo_correcao)}`}
                  </div>
                </div>
                <button
                  onClick={() => gerarChamado(item)}
                  disabled={gerando === item.id}
                  style={{
                    background: '#e65100', color: '#fff', border: 'none', borderRadius: 6,
                    padding: '8px 12px', cursor: 'pointer', fontWeight: 600,
                  }}
                >
                  {gerando === item.id ? 'Abrindo…' : 'Abrir chamado'}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ ...card, display: 'flex', gap: 28, flexWrap: 'wrap' }}>
        <Numero valor={resumo.patrimonio.total} label="bens de patrimônio" cor="#1565c0" />
        {Object.entries(resumo.patrimonio.porEstado).map(([estado, qtd]) => (
          <Numero
            key={estado}
            valor={qtd}
            label={LABEL_ESTADO_PATRIMONIO[estado] || estado}
            cor={estado === 'em_manutencao' ? '#e65100' : estado === 'em_uso' ? '#2e7d32' : '#616161'}
          />
        ))}
      </div>
    </>
  );
}
